import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import Layout from "@/components/feature/Layout";
import SEO from "@/components/feature/SEO";
import { contactInfo } from "@/mocks/home";

export default function ContactPage() {
  const { t } = useTranslation("home");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();

    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch("https://readdy.ai/api/form/d7udo7gng1a8777gd4hg", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: data.toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        setError(false);
        form.reset();
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    }
  };

  return (
    <Layout>
      <SEO
        title="Contacto Ventra Core | HVAC, Eléctricos y UPS en León, Guanajuato"
        description="Contáctanos para cotizaciones de sistemas HVAC, climatización, sistemas eléctricos especializados y energía UPS en León, Guanajuato. Teléfono +52 477 284 70 60."
        keywords="contacto Ventra Core, HVAC León, climatización Guanajuato, sistemas eléctricos, energía UPS, cotización HVAC"
        canonical="https://ventracore.com.mx/contacto"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "@id": "https://ventracore.com.mx/contacto#webpage",
            "url": "https://ventracore.com.mx/contacto",
            "name": "Contacto Ventra Core",
            "isPartOf": { "@id": "https://ventracore.com.mx/#website" },
            "description": "Página de contacto de Ventra Core: HVAC, climatización y energía UPS en León, Guanajuato.",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Inicio",
                "item": "https://ventracore.com.mx/",
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Contacto",
                "item": "https://ventracore.com.mx/contacto",
              },
            ],
          },
        ]}
      />
      <div className="w-full">
        {/* Hero */}
        <section className="relative w-full bg-brandgreen py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundRepeat: "repeat",
              }}
            />
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 uppercase tracking-wide">
              {t("contactPage.title")}
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto">
              {t("contactPage.subtitle")}
            </p>
            <div className="w-20 h-1 bg-white/50 mx-auto mt-6" />
          </div>
        </section>

        {/* Contact Content */}
        <section className="w-full px-4 md:px-6 py-12 md:py-20 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
              {/* Form */}
              <div className="w-full lg:w-3/5">
                <h2 className="text-xl md:text-2xl font-bold text-magenta mb-6 uppercase tracking-wide">
                  {t("contactPage.formTitle")}
                </h2>

                {submitted && (
                  <div className="mb-6 bg-brandgreen/10 border border-brandgreen/30 rounded-md p-4 flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center text-brandgreen shrink-0 mt-0.5">
                      <i className="ri-check-line" />
                    </span>
                    <p className="text-sm text-brandgreen-dark font-medium">
                      {t("contactPage.successMessage")}
                    </p>
                  </div>
                )}

                {error && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4 flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center text-red-500 shrink-0 mt-0.5">
                      <i className="ri-error-warning-line" />
                    </span>
                    <p className="text-sm text-red-600 font-medium">
                      {t("contactPage.errorMessage")}
                    </p>
                  </div>
                )}

                <form
                  id="contacto-ventracore"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-text mb-1">
                        {t("contactPage.nameLabel")} *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brandgreen/30 focus:border-brandgreen transition-colors"
                        placeholder={t("contactPage.namePlaceholder")}
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-text mb-1">
                        {t("contactPage.emailLabel")} *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brandgreen/30 focus:border-brandgreen transition-colors"
                        placeholder={t("contactPage.emailPlaceholder")}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-text mb-1">
                        {t("contactPage.phoneLabel")}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brandgreen/30 focus:border-brandgreen transition-colors"
                        placeholder={t("contactPage.phonePlaceholder")}
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-text mb-1">
                        {t("contactPage.companyLabel")}
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brandgreen/30 focus:border-brandgreen transition-colors"
                        placeholder={t("contactPage.companyPlaceholder")}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-text mb-1">
                      {t("contactPage.serviceLabel")}
                    </label>
                    <select
                      id="service"
                      name="service"
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brandgreen/30 focus:border-brandgreen transition-colors bg-white"
                    >
                      <option value="">{t("contactPage.serviceSelect")}</option>
                      <option value="hvac">HVAC</option>
                      <option value="congelacion">{t("contactPage.serviceCongelacion")}</option>
                      <option value="electrico">{t("contactPage.serviceElectrico")}</option>
                      <option value="ups">{t("contactPage.serviceUps")}</option>
                      <option value="coolnet">Coolnet</option>
                      <option value="instalacion">{t("contactPage.serviceInstalacion")}</option>
                      <option value="general">{t("contactPage.serviceGeneral")}</option>
                      <option value="otro">{t("contactPage.serviceOtro")}</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-text mb-1">
                      {t("contactPage.messageLabel")} *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brandgreen/30 focus:border-brandgreen transition-colors resize-none"
                      placeholder={t("contactPage.messagePlaceholder")}
                    />
                    <p className="text-xs text-text-light mt-1">
                      {t("contactPage.maxChars")}
                    </p>
                  </div>

                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 bg-brandgreen text-white font-semibold px-8 py-3 rounded-md hover:bg-brandgreen-dark transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-send-plane-line" />
                    </span>
                    {t("contactPage.submitButton")}
                  </button>
                </form>
              </div>

              {/* Contact Info */}
              <div className="w-full lg:w-2/5">
                <div className="bg-surface rounded-lg p-6 md:p-8">
                  <h3 className="text-lg md:text-xl font-bold text-magenta mb-6 uppercase tracking-wide">
                    {t("contactPage.infoTitle")}
                  </h3>

                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex items-center justify-center bg-brandgreen/10 rounded-md shrink-0">
                        <i className="ri-map-pin-line text-brandgreen" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-text">{t("contactPage.addressLabel")}</p>
                        <p className="text-sm text-text-muted leading-relaxed">
                          {t("contactPage.address")}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex items-center justify-center bg-brandgreen/10 rounded-md shrink-0">
                        <i className="ri-phone-line text-brandgreen" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-text">{t("contactPage.phoneLabel")}</p>
                        <a
                          href={contactInfo.phoneHref}
                          className="text-sm text-text-muted hover:text-brandgreen transition-colors"
                          rel="nofollow"
                        >
                          {contactInfo.phone}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex items-center justify-center bg-brandgreen/10 rounded-md shrink-0">
                        <i className="ri-mail-line text-brandgreen" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-text">{t("contactPage.emailLabel")}</p>
                        <a
                          href={contactInfo.emailHref}
                          className="text-sm text-text-muted hover:text-brandgreen transition-colors"
                          rel="nofollow"
                        >
                          {contactInfo.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex items-center justify-center bg-brandgreen/10 rounded-md shrink-0">
                        <i className="ri-whatsapp-line text-brandgreen" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-text">WhatsApp</p>
                        <a
                          href={contactInfo.whatsapp}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="text-sm text-text-muted hover:text-brandgreen transition-colors"
                        >
                          +52 477 284 70 60
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex items-center justify-center bg-brandgreen/10 rounded-md shrink-0">
                        <i className="ri-time-line text-brandgreen" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-text">{t("contactPage.hoursLabel")}</p>
                        <p className="text-sm text-text-muted leading-relaxed">
                          {t("contactPage.hours")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="w-full bg-surface">
          <div className="w-full h-[350px] md:h-[450px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3721.8523565!2d-101.6734!3d21.1234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjFBsDA3JzI0LjIiTiAxMDHCsDQwJzI0LjIiVw!5e0!3m2!1ses!2smx!4v1700000000000!5m2!1ses!2smx"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ubicación Ventra Core"
              aria-label="Mapa de ubicación de Ventra Core en León, Guanajuato"
            />
          </div>
        </section>

        {/* CTA */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-magenta">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-4 uppercase tracking-wide">
              {t("contactPage.ctaTitle")}
            </h3>
            <p className="text-sm md:text-base text-white/80 mb-6">
              {t("contactPage.ctaDesc")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+524772847060"
                className="inline-flex items-center gap-2 bg-white text-magenta font-semibold px-6 py-3 rounded-md hover:bg-surface transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line" />
                </span>
                +52 477 284 70 60
              </a>
              <a
                href={contactInfo.whatsapp}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 bg-white text-magenta font-semibold px-6 py-3 rounded-md hover:bg-surface transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line" />
                </span>
                WhatsApp
              </a>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}